/**
 * v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40: the redirect passes /api/me connectSkipped to firstScreenFor (the same
 *                      input App.tsx passes), so a company that skipped Connect your data goes to the dashboard.
 * v4.30 - 2026-10-15 - ROW 69 (ruling 10): a 409 tenant_state_incomplete from /api/me is logged and goes to the dashboard layout, which says the setup is incomplete.
 * v4.29 - 2026-10-13 - ONBOARDING-NOT-ENFORCED-01: getDestination is now a thin wrapper around
 *                      firstScreenFor (services/firstScreen.ts); the rule that lived here is now the
 *                      one place every signed-in redirect reads. No behaviour change for this page —
 *                      v2.82's rule was already the correct one — but any future tweak now has to
 *                      happen in firstScreen.ts and every caller gets it.
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01 T4): a person who came from an invite link goes back to that invite
 *                      (return path, consumed once after /api/me answers; the 404 bypass is gone because a pending invite now answers 200); a 409 company_choice_required goes to the dashboard layout, which shows the choice
 * AuthLanding Page - Intermediate page after Clerk authentication
 *
 * Version History:
 * v3.13 - 2026-02-14 - Fix #1: Show spinner (not Welcome UI) while auto-redirect is pending
 * v2.85 - 2026-02-05 - Always auto-redirect + use replace() so /auth-landing is never in back stack
 * v2.82 - 2026-02-05 - Auto-redirect when state is known (no click needed on refresh/return visits)
 * v2.79 - 2026-02-05 - Added captiveComplete check for routing (captive flow takes priority)
 * v2.74 - 2026-02-05 - Use X-Authorization header (SWA intercepts standard Authorization)
 * v2.69 - 2026-02-05 - Fixed infinite loop: don't redirect on 401 if signed in, use ref to prevent re-fetching
 * v2.65 - 2026-02-05 - Wait for isSignedIn before getting token, add retry logic
 * v2.64 - 2026-02-05 - Fixed to use Clerk auth hook for /api/me token
 * v2.55 - 2026-02-04 - Updated for Clerk authentication migration
 * v2.22 - 2026-01-25 - Handle discovery_complete state to resume at Connect Data step
 * v2.18 - 2026-01-24 - Chrome HMI fix: requires user gesture before navigation
 *
 * Purpose:
 * Chrome's History Manipulation Intervention (HMI) marks automated redirects/pushState
 * as "skippable" if done without user interaction. This page requires a user click
 * before navigating to dashboard/onboarding, satisfying Chrome's "user gesture"
 * requirement and making subsequent history entries non-skippable.
 *
 * Flow:
 * 1. User signs in via Clerk (Login.tsx or Consent.tsx)
 * 2. Clerk redirects to /auth-landing
 * 3. This page fetches /api/me to get workflow state (with Clerk token via X-Authorization)
 * 4. User clicks "Continue" -> navigates to /dashboard or /onboarding
 *
 * IMPORTANT: Azure SWA intercepts and overwrites the standard Authorization header.
 * We use X-Authorization instead. See:
 * - https://github.com/Azure/static-web-apps/issues/158
 * - https://github.com/Azure/static-web-apps/issues/275
 */
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, Button, CircularProgress, Alert } from '@mui/material';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import LoginIcon from '@mui/icons-material/Login';
import ErrorIcon from '@mui/icons-material/Error';
import { failClosedClerkSession, consumeReturnPath } from '../services/authGate';
import { portalRequestUngated, logClientDecision } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company
import { firstScreenFor } from '../services/firstScreen'; // v4.29 - ONBOARDING-NOT-ENFORCED-01: one first-screen rule
import { parsePendingInvite, pendingInviteTarget } from '../hooks/usePermissions';

const AuthLanding: React.FC = () => {
  const { getToken, isLoaded: authLoaded, isSignedIn, signOut, sessionId } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [workflowState, setWorkflowState] = useState<string | null>(null);
  const [captiveComplete, setCaptiveComplete] = useState<string | null>(null);  // v2.79 - Captive flag
  const [connectSkipped, setConnectSkipped] = useState<boolean>(false);  // v4.39 - row 40
  const [error, setError] = useState<string | null>(null);

  // v2.69 - Use ref to prevent multiple fetch attempts
  const hasFetched = useRef(false);
  const retryCount = useRef(0);

  useEffect(() => {
    // v2.69 - Prevent re-running if we've already fetched or are in progress
    if (hasFetched.current) {
      return;
    }

    // Wait for Clerk to load
    if (!authLoaded) {
      console.log('AuthLanding: Waiting for Clerk to load...');
      return;
    }

    // If not signed in, redirect to login (but only once)
    if (!isSignedIn) {
      console.log('AuthLanding: Not signed in, redirecting to login');
      hasFetched.current = true;
      navigate('/login', { replace: true });
      return;
    }

    // Fetch workflow state
    let active = true;
    const checkWorkflow = async () => {
      if (!active) return;
      try {
        const token = await getToken();
        console.log('AuthLanding: Clerk token obtained:', token ? `yes (${token.substring(0, 20)}...)` : 'no');

        if (!token) {
          retryCount.current += 1;
          if (retryCount.current < 3) {
            console.log(`AuthLanding: No token yet, retry ${retryCount.current}/3 in 500ms...`);
            setTimeout(checkWorkflow, 500);
            return;
          }
          // v2.69 - After retries, show error instead of redirecting (to avoid loop)
          console.error('AuthLanding: No token after retries');
          setError('Unable to verify your session. Please try signing out and back in.');
          setIsChecking(false);
          hasFetched.current = true;
          return;
        }

        // v2.74 - Use X-Authorization instead of Authorization
        // Azure SWA intercepts Authorization header and replaces it with its own internal token
        // See: https://github.com/Azure/static-web-apps/issues/158
        console.log('AuthLanding: Fetching /api/me...');
        const response = await portalRequestUngated('/api/me', {
          headers: { 'X-Authorization': `Bearer ${token}` },
          credentials: 'include',
        });

        if (!active) return;
        console.log('AuthLanding: /api/me response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          // v4.28 - G10-B: the /api/me body is no longer printed (pendingInvite.acceptPath carries the invite token)
          // v4.28 - G10-B (T4): a person who came from an invite link goes back to that invite, once /api/me has
          // answered. (A person with no company and a pending invite now gets a 200 with pendingInvite instead of a 404,
          // so no bypass of /api/me is needed.)
          // The server's acceptPath (own verified email only) is the first choice, then the remembered link.
          const pending = !data.role ? parsePendingInvite(data.pendingInvite) : null;
          const returnPath = pendingInviteTarget(pending, consumeReturnPath());
          if (returnPath) {
            if (pending) logClientDecision(pending.acceptPath ? 'pending_invite_redirect_accept_path' : 'pending_invite_redirect', { invite: pending.inviteId });
            hasFetched.current = true;
            navigate(returnPath, { replace: true });
            return;
          }
          // No company yet and a pending invite, but no link: the dashboard layout says where the invitation is.
          if (pending) {
            hasFetched.current = true;
            navigate('/dashboard', { replace: true });
            return;
          }
          setWorkflowState(data.workflowState || 'signed_up');
          setCaptiveComplete(data.captiveComplete || null);  // v2.79
          setConnectSkipped(data.connectSkipped === true);  // v4.39 - row 40
          setIsChecking(false);
          hasFetched.current = true;
        } else {
          const errorText = await response.text();
          console.error('AuthLanding: /api/me failed:', response.status, errorText);

          // v4.28 - G10-B: 409 company_choice_required - the dashboard layout shows the choice
          // v4.30 - ROW 69 (ruling 10): 409 tenant_state_incomplete goes to the same layout, which says the setup is incomplete
          if (response.status === 409) {
            if (errorText.includes('tenant_state_incomplete')) logClientDecision('setup_incomplete', { route: '/api/me' });
            hasFetched.current = true;
            navigate('/dashboard', { replace: true });
            return;
          }

          if (response.status === 401 || response.status === 404) {
            hasFetched.current = true;
            await failClosedClerkSession(signOut, (url) => navigate(url, { replace: true }), sessionId || null);
            return;
          }

          setError(`Authentication failed (${response.status}). The server could not verify your session. Please try signing out and back in.`);
          setIsChecking(false);
          hasFetched.current = true;
        }
      } catch (err) {
        if (!active) return;
        console.error('AuthLanding: Failed to fetch workflow state:', err);
        setError('Network error. Please check your connection and try again.');
        setIsChecking(false);
        hasFetched.current = true;
      }
    };

    console.log('AuthLanding: Clerk loaded and signed in, checking workflow...');
    checkWorkflow();
    return () => { active = false; };
  }, [authLoaded, isSignedIn, getToken, navigate, signOut, sessionId]);

  // v4.29 - ONBOARDING-NOT-ENFORCED-01: firstScreenFor is the one place the rule lives.
  // The route table in App.tsx reads the same function.
  const getDestination = (): string => firstScreenFor({ captiveComplete, workflowState, connectSkipped }); // v4.39 - row 40

  // v2.85 - ALWAYS auto-redirect. Never show "Welcome" screen.
  // Use replace() so /auth-landing is never in the browser back stack.
  useEffect(() => {
    if (!isChecking && !error && workflowState) {
      console.log('v2.85: Auto-redirecting (always):', { captiveComplete, workflowState });
      window.location.replace(getDestination());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecking, error, workflowState, captiveComplete, connectSkipped]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleContinue = () => {
    // v2.85 - Use replace() so /auth-landing is never in the back stack
    window.location.replace(getDestination());
  };

  const handleSignOut = async () => {
    await failClosedClerkSession(signOut, (url) => navigate(url, { replace: true }));
  };

  // Show loading spinner while checking
  if (isChecking) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main', mb: 2 }}>
              Authentication Error
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              size="large"
              onClick={handleSignOut}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 6,
                py: 1.5,
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              Sign Out and Try Again
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // v3.13 - Fix #1: When workflowState is set, auto-redirect useEffect will fire next frame.
  // Show spinner instead of the old "Welcome" UI to avoid the flash.
  // Fallback: if redirect hasn't fired after 2s, show Continue button as escape hatch.
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress color="primary" />
    </Box>
  );
};

export default AuthLanding;
